/*
  IT Recycling — cart page behaviour.

  Loaded only by the cart template. It adds two things Dawn does not ship and
  deliberately leaves everything Dawn does own alone: quantity updates, line
  removal, the checkout submit and the live regions are still handled by cart.js.

  Both elements are progressive enhancements. Without JavaScript the delivery
  radios and the workshop message still reach the cart, because they are ordinary
  `attributes[...]` fields on the `#cart` form, and the accessory cards still add
  to the cart, because they are ordinary `/cart/add` forms with `return_to`.

  Both cart sections request this file -- main-cart-items for the delivery radios
  and the accessory cards, main-cart-footer for the workshop message -- so on the
  cart page the browser executes it twice. Each section keeps its own tag rather
  than relying on the other being present, so the body is wrapped and exits early
  on the second run. Without that guard the second run threw on the top-level
  declarations and neither custom element ever registered.
*/

(() => {
  if (customElements.get('cart-attribute-fields')) return;

  const ITR_CART_SECTION_IDS = () =>
    ['main-cart-items', 'main-cart-footer'].map((id) => document.getElementById(id)).filter(Boolean);

  function itrCartRenderSection(element, sectionsHtml) {
    const html = sectionsHtml && sectionsHtml[element.dataset.id];
    if (!html) return;

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const source = parsed.querySelector('.js-contents');
    const target = element.querySelector('.js-contents');
    if (source && target) target.innerHTML = source.innerHTML;
  }

  /*
    Saves one cart attribute. Radios ask for a re-render because the order summary
    names the chosen delivery method; the message textarea does not, so typing is
    never interrupted by a DOM swap.
  */
  class CartAttributeFields extends HTMLElement {
    constructor() {
      super();

      this.addEventListener('change', (event) => {
        const field = event.target.closest('[data-cart-attribute]');
        if (!field || field.tagName === 'TEXTAREA') return;
        this.save(field, true);
      });

      this.addEventListener(
        'input',
        debounce((event) => {
          const field = event.target.closest('[data-cart-attribute]');
          if (!field || field.tagName !== 'TEXTAREA') return;
          this.save(field, false);
        }, ON_CHANGE_DEBOUNCE_TIMER),
      );
    }

    save(field, refresh) {
      const sectionElements = refresh ? ITR_CART_SECTION_IDS() : [];
      const attributes = {};
      attributes[field.dataset.cartAttribute] = field.value;

      const body = JSON.stringify({
        attributes,
        sections: sectionElements.map((element) => element.dataset.id),
        sections_url: window.location.pathname,
      });

      fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
        .then((response) => response.json())
        .then((cart) => {
          if (!cart || cart.errors) throw new Error(cart && cart.errors);
          sectionElements.forEach((element) => itrCartRenderSection(element, cart.sections));
        })
        .catch((error) => {
          console.error(error);
        });
    }
  }

  customElements.define('cart-attribute-fields', CartAttributeFields);

  /*
    Accessory "Tilføj" card. Adds the variant over AJAX, repaints the cart page's
    own two sections and the header bubble, then publishes the same cartUpdate the
    rest of the theme already listens to so the drawer stays in step.
  */
  class CartAccessoryForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      this.submitButton = this.querySelector('[type="submit"]');
      this.errorElement = this.querySelector('.itr-cart__extra-error');

      if (this.form) this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    onSubmit(event) {
      event.preventDefault();
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

      this.submitButton.setAttribute('aria-disabled', 'true');
      if (this.errorElement) this.errorElement.textContent = '';

      const sectionElements = ITR_CART_SECTION_IDS();
      const bubble = document.getElementById('cart-icon-bubble');
      const sections = sectionElements.map((element) => element.dataset.id);
      if (bubble) sections.push('cart-icon-bubble');

      const formData = new FormData(this.form);
      formData.append('sections', sections.join(','));
      formData.append('sections_url', window.location.pathname);

      const config = fetchConfig('javascript');
      delete config.headers['Content-Type'];
      config.body = formData;

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            if (this.errorElement) this.errorElement.textContent = response.description || response.message;
            return;
          }

          sectionElements.forEach((element) => itrCartRenderSection(element, response.sections));

          if (bubble && response.sections && response.sections['cart-icon-bubble']) {
            const parsed = new DOMParser().parseFromString(response.sections['cart-icon-bubble'], 'text/html');
            const source = parsed.querySelector('.shopify-section');
            if (source) bubble.innerHTML = source.innerHTML;
          }

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-accessory-form',
            productVariantId: formData.get('id'),
            cartData: response,
          });
        })
        .catch((error) => {
          console.error(error);
          if (this.errorElement) this.errorElement.textContent = window.cartStrings.error;
        })
        .finally(() => {
          this.submitButton.setAttribute('aria-disabled', 'false');
        });
    }
  }

  customElements.define('cart-accessory-form', CartAccessoryForm);
})();
